import { lazy, Suspense } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { DataError } from '../data/http';
import { normaliseParcelNo } from '../data/parcelNo';
import { dataVersion, REGIONS } from '../data/region';
import { t } from '../i18n';
import { SiteFooter, SiteHeader } from '../site/Shell';
import { EngineFailure, runInWorker } from '../worker/client';
import { durationBucket, track } from './analytics';
import { Landing, type LandingError } from './Landing';
import { Progress, type StepKey } from './Progress';
import { titleCase } from '../format';
import type { ReportData } from './types';

// the report UI and the data layer (geotiff, pmtiles, proj4, polygon clipping) load only when a parcel is requested
const Report = lazy(() => import('./Report').then((m) => m.Report));
const loadData = () => import('../data/load');

type View =
  | { kind: 'landing'; ko?: string; parcel?: string; error?: LandingError | null }
  | { kind: 'progress'; title: string; step: StepKey; frac: number }
  | { kind: 'report'; data: ReportData };

function errorFor(e: unknown, ko: number, st: string): LandingError {
  if (e instanceof DataError) {
    const det = (e.detail ?? {}) as any;
    switch (e.code) {
      case 'ko_unknown': return { code: e.code, message: t.err.koUnknown, covered: det.covered };
      case 'parcel_unknown': return { code: e.code, message: t.err.parcelUnknown(st, `${det.ko?.id ?? ko} ${titleCase(det.ko?.name ?? '')}`.trim()) };
      case 'outside_region': return { code: e.code, message: t.err.outside, ko, st, region: det.ko?.region };
      case 'too_large': return { code: e.code, message: t.err.tooLarge };
      case 'nodata': return { code: e.code, message: t.err.nodata };
      default: return { code: 'fetch', message: t.err.fetch };
    }
  }
  if (e instanceof EngineFailure && e.code === 'nodata') return { code: 'nodata', message: t.err.nodata };
  console.error(e);
  return { code: 'engine', message: t.err.generic };
}

function readUrl() {
  const q = new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
  return { ko: q.get('ko') ?? undefined, p: q.get('p') ?? undefined };
}

/** Report URLs (?ko=…&p=…) open straight into the progress view, without a flash of the landing page. */
export const isReportUrl = () => { const { ko, p } = readUrl(); return !!(ko && p && /^\d+$/.test(ko)); };

/** Reports are per-parcel and computed client side: keep them out of search indexes, keep the landing indexable. */
function setIndexable(on: boolean) {
  let m = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!m) { m = document.createElement('meta'); m.name = 'robots'; document.head.append(m); }
  m.content = on ? 'index, follow' : 'noindex, follow';
}

export function App() {
  const [view, setView] = useState<View>(() => (isReportUrl()
    ? { kind: 'progress', title: t.progressTitle(normaliseParcelNo(readUrl().p!), readUrl().ko!), step: 'find', frac: 0 }
    : { kind: 'landing' }));
  const runId = useRef(0);
  const shown = useRef('');  // ko|p of the current view (other query params, e.g. the roof selection, don't change it)
  const urlKey = () => { const { ko, p } = readUrl(); return `${ko}|${p}`; };

  const start = async (ko: number, parcelRaw: string, push: boolean) => {
    const st = normaliseParcelNo(parcelRaw);
    if (push) history.pushState(null, '', `?ko=${ko}&p=${encodeURIComponent(st)}`);
    shown.current = urlKey();
    const id = ++runId.current;
    const t0 = performance.now();
    let step: StepKey = 'find';
    const title = t.progressTitle(st, String(ko));
    const show = (s: StepKey, frac = 0) => { step = s; if (id === runId.current) setView({ kind: 'progress', title, step: s, frac }); };
    show('find');
    setIndexable(false);
    track('report_started');
    try {
      const { loadParcel } = await loadData();
      const L = await loadParcel(ko, st, (s) => show(s));
      if (id !== runId.current) return;
      setView({ kind: 'progress', title: t.progressTitle(st, `${ko} ${titleCase(L.hit.ko.name)}`), step: 'horizon', frac: 0 });
      step = 'horizon';
      const versionP = dataVersion(L.hit.ko.region);
      let lastPaint = 0;
      const out = await runInWorker(L.input, (stage, done, total) => {
        const now = performance.now();
        if (now - lastPaint < 60 && done < total) return;
        lastPaint = now;
        step = stage;
        if (id === runId.current) setView({ kind: 'progress', title: t.progressTitle(st, `${ko} ${titleCase(L.hit.ko.name)}`), step: stage, frac: done / total });
      });
      if (id !== runId.current) return;
      show('report');
      const version = await versionP;
      const region = REGIONS.find((r) => r.id === L.hit.ko.region) ?? { id: L.hit.ko.region, obcina: L.hit.ko.region };
      document.title = `${t.appName} · ${t.title(st, ko, titleCase(L.hit.ko.name))}`;
      setView({
        kind: 'report',
        data: { out, hit: L.hit, region, outer: L.outer, buildings: L.buildings, window: L.window, lat: L.input.tmy.lat, lon: L.input.tmy.lon, version, input: L.input },
      });
      track('report_done', { duration: durationBucket(performance.now() - t0) });
    } catch (e) {
      if (id !== runId.current) return;
      track('report_failed', { step });
      setView({ kind: 'landing', ko: String(ko), parcel: parcelRaw, error: errorFor(e, ko, st) });
    }
  };

  const fromUrl = () => {
    shown.current = urlKey();
    const { ko, p } = readUrl();
    if (ko && p && /^\d+$/.test(ko)) start(+ko, p, false);
    else { runId.current++; document.title = t.site.metaTitle; setIndexable(true); setView({ kind: 'landing', ko, parcel: p }); }
  };

  useEffect(() => {
    fromUrl();
    // in-page anchors (#povzetek, #ponudba …) fire popstate too; only a changed ?ko=…&p=… is a new view
    const onPop = () => { if (urlKey() !== shown.current) fromUrl(); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [view.kind]);

  const onNew = () => { history.pushState(null, '', '/'); fromUrl(); };
  if (view.kind === 'progress') {
    return <><SiteHeader variant="app" /><Progress title={view.title} step={view.step} frac={view.frac} /></>;
  }
  if (view.kind === 'report') {
    return (
      <>
        <SiteHeader variant="app" onNew={onNew} />
        <Suspense fallback={<Progress title={t.title(view.data.hit.st, view.data.hit.ko.id, titleCase(view.data.hit.ko.name))} step="report" frac={1} />}>
          <Report d={view.data} />
        </Suspense>
        <SiteFooter />
      </>
    );
  }
  return (
    <>
      <SiteHeader variant="home" />
      <Landing
        key={`${view.ko}|${view.parcel}|${view.error?.code ?? ''}`}
        initialKo={view.ko} initialParcel={view.parcel} error={view.error}
        onSubmit={(ko, p) => start(ko, p, true)}
        onRetry={view.ko && view.parcel ? () => start(+view.ko!, view.parcel!, false) : undefined}
      />
      <SiteFooter />
    </>
  );
}
